/**
 * Spec 028: countries, US states and Canadian provinces, and the tax label a
 * receipt shows for a pickup location.
 */

import { US_STATE_NAMES } from './usStates';

export type CountryCode = 'US' | 'CA';

/** Reads a stored country; anything but CA is US (rows from before spec 028). */
export const asCountry = (value: unknown): CountryCode =>
  String(value ?? '')
    .trim()
    .toUpperCase() === 'CA'
    ? 'CA'
    : 'US';

/** Spec 022's list, kept in `usStates.ts` for its existing imports. */
export { US_STATE_NAMES };

export const CA_PROVINCE_NAMES: Readonly<Record<string, string>> = Object.freeze({
  AB: 'Alberta',
  BC: 'British Columbia',
  MB: 'Manitoba',
  NB: 'New Brunswick',
  NL: 'Newfoundland and Labrador',
  NS: 'Nova Scotia',
  ON: 'Ontario',
  PE: 'Prince Edward Island',
  QC: 'Quebec',
  SK: 'Saskatchewan',
  NT: 'Northwest Territories',
  NU: 'Nunavut',
  YT: 'Yukon',
});

export const regionNames = (country: CountryCode | string | null | undefined) =>
  asCountry(country) === 'CA' ? CA_PROVINCE_NAMES : US_STATE_NAMES;

/** The full name of a state or province code, else null. */
export const regionName = (
  country: CountryCode | string | null | undefined,
  code: string | null | undefined,
): string | null => regionNames(country)[(code ?? '').trim().toUpperCase()] ?? null;

const HST_PROVINCES = new Set(['ON', 'NB', 'NL', 'NS', 'PE']);
const GST_ONLY = new Set(['AB', 'NT', 'NU', 'YT']);

/**
 * Spec D16: the tax line's label. Only the label: the rate stays the location's
 * `taxRate`. BC, MB, SK and QC charge GST plus a provincial tax, which this
 * spec does not split, so they read "Tax" like the US.
 */
export const taxLabelFor = (
  country: CountryCode | string | null | undefined,
  region: string | null | undefined,
): string => {
  if (asCountry(country) !== 'CA') return 'Tax';
  const code = (region ?? '').trim().toUpperCase();
  if (HST_PROVINCES.has(code)) return 'HST';
  if (GST_ONLY.has(code)) return 'GST';
  return 'Tax';
};

/**
 * Spec 028: a US state's or a Canadian province's full name from its code.
 * The two code lists do not overlap, so no country is needed (the pickup
 * catalog rows carry none). Null for anything else.
 */
export const stateOrProvinceName = (code: string | null | undefined): string | null => {
  const key = (code ?? '').trim().toUpperCase();
  return US_STATE_NAMES[key] ?? CA_PROVINCE_NAMES[key] ?? null;
};
